import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle, CheckCircle2, Calculator as CalcIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  kryzys: { color: "#d84a4a", icon: AlertTriangle, label: "Kryzys" },
  zagrożenie: { color: "#e89c3d", icon: AlertTriangle, label: "Zagrożenie" },
  stabilnie: { color: "#d4a94a", icon: TrendingUp, label: "Stabilnie" },
  zdrowo: { color: "#76c26c", icon: CheckCircle2, label: "Zdrowo" },
};

const initial = {
  monthly_revenue: "",
  fuel_costs: "",
  drivers_costs: "",
  leasing_costs: "",
  other_costs: "",
};

export default function Calculator() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, parseFloat(v) || 0])
    );
    if (payload.monthly_revenue <= 0) {
      toast.error("Podaj miesięczny przychód firmy");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/calc/profitability`, payload);
      setResult(data);
      setTimeout(() => {
        document
          .getElementById("calc-result")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);
    } catch (err) {
      toast.error("Coś poszło nie tak. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(initial);
    setResult(null);
  };

  const fields = [
    { k: "monthly_revenue", l: "Miesięczny przychód", ph: "np. 250000" },
    { k: "fuel_costs", l: "Koszty paliwa", ph: "np. 70000" },
    { k: "drivers_costs", l: "Koszty kierowców", ph: "np. 80000" },
    { k: "leasing_costs", l: "Leasingi / raty", ph: "np. 40000" },
    { k: "other_costs", l: "Pozostałe koszty", ph: "np. 30000" },
  ];

  const status = result ? STATUS_META[result.status] : null;
  const StatusIcon = status?.icon;

  return (
    <section id="kalkulator" data-testid="calculator-section" className="section relative">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">Narzędzie • Bezpłatne</span>
            <h2 className="h-display mt-4 text-[clamp(28px,3.6vw,44px)]">
              Czy Twoja firma{" "}
              <span className="gold-gradient-text italic">zarabia?</span>
            </h2>
            <div className="gold-line mt-6" />
            <p className="mt-8 text-[16px] leading-relaxed text-[#b8b3a7]">
              Wprowadź podstawowe dane finansowe z ostatniego miesiąca.
              Dowiesz się, jak wygląda Twoja rentowność i co można poprawić —
              w mniej niż 60 sekund.
            </p>
            <div className="mt-10 flex items-center gap-3">
              <CalcIcon className="text-[var(--gold)]" size={20} />
              <span className="text-[13px] tracking-[0.22em] uppercase gold-text">
                100% poufne • nie zapisujemy danych
              </span>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="calc-form"
              className="card-dark p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {fields.map((f, i) => (
                  <div
                    key={f.k}
                    className={i === 0 ? "sm:col-span-2" : ""}
                  >
                    <label className="lbl" htmlFor={`calc-${f.k}`}>{f.l} (zł)</label>
                    <input
                      id={`calc-${f.k}`}
                      data-testid={`calc-input-${f.k}`}
                      type="number"
                      min="0"
                      step="any"
                      placeholder={f.ph}
                      value={form[f.k]}
                      onChange={update(f.k)}
                      className="field"
                    />
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="calc-submit"
                  className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Analizuję..." : "Sprawdź rentowność"}
                  <span aria-hidden>→</span>
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={reset}
                    className="btn-ghost"
                    data-testid="calc-reset"
                  >
                    Wyczyść
                  </button>
                )}
              </div>
            </form>

            {result && status && (
              <div
                id="calc-result"
                data-testid="calc-result"
                className="card-dark p-8 lg:p-10 mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
              >
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div>
                    <span className="eyebrow">Wynik analizy</span>
                    <div className="mt-3 flex items-center gap-3">
                      <StatusIcon style={{ color: status.color }} size={24} />
                      <span
                        className="font-display text-[28px] md:text-[34px]"
                        style={{ color: status.color }}
                      >
                        {status.label}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-[#b8b3a7]">
                      Marża netto
                    </div>
                    <div className="stat-number mt-1">{result.margin_pct}%</div>
                  </div>
                </div>

                <p className="mt-6 text-[16px] leading-relaxed text-[#e8e3d5]">
                  {result.message}
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="border border-[rgba(212,169,74,0.18)] p-5">
                    <div className="text-[10px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                      Przychód
                    </div>
                    <div className="font-display text-[22px] text-[#f5f1e8] mt-2">
                      {result.revenue.toLocaleString("pl-PL")} zł
                    </div>
                  </div>
                  <div className="border border-[rgba(212,169,74,0.18)] p-5">
                    <div className="text-[10px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                      Koszty razem
                    </div>
                    <div className="font-display text-[22px] text-[#f5f1e8] mt-2">
                      {result.total_costs.toLocaleString("pl-PL")} zł
                    </div>
                  </div>
                  <div className="border border-[rgba(212,169,74,0.18)] p-5">
                    <div className="text-[10px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                      Zysk
                    </div>
                    <div
                      className="font-display text-[22px] mt-2"
                      style={{ color: result.profit >= 0 ? "var(--gold)" : "#d84a4a" }}
                    >
                      {result.profit.toLocaleString("pl-PL")} zł
                    </div>
                  </div>
                </div>

                {result.recommendations?.length > 0 && (
                  <div className="mt-8">
                    <div className="text-[11px] tracking-[0.26em] uppercase gold-text mb-4">
                      Rekomendacje
                    </div>
                    <ul className="space-y-3">
                      {result.recommendations.map((r, i) => (
                        <li
                          key={i}
                          className="flex gap-3 text-[15px] text-[#c9c4b8] leading-relaxed"
                        >
                          <span className="text-[var(--gold)] font-display">→</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-8 pt-8 border-t border-[rgba(212,169,74,0.15)]">
                  <p className="text-[14px] text-[#b8b3a7] mb-4">
                    Chcesz pogłębioną analizę i konkretny plan naprawczy?
                  </p>
                  <button
                    onClick={() => navigate("/kontakt")}
                    className="btn-gold"
                    data-testid="calc-cta-contact"
                  >
                    Umów bezpłatną rozmowę →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
